import type { TFunction } from 'i18next';
import type { UnitDecisionsListItem } from '@ooh/contracts';
import { buildMediaUnitFieldColumns } from '../../../lib/media-unit-field-columns';
import {
  OFFER_MEDIA_UNIT_SORT_KEY_ALIASES,
  type OfferMediaUnitFieldId,
} from '../../../lib/offer-media-unit-fields';

function getValue(row: UnitDecisionsListItem, fieldId: OfferMediaUnitFieldId): unknown {
  const values: Partial<Record<OfferMediaUnitFieldId, unknown>> = {
    mediaType: row.mediaType,
    formatCode: row.formatCode,
    supplierId: row.supplier,
    supplierNumber: row.supplierNumber,
    grwId: row.grwId,
    region: row.region,
    county: row.county,
    agglomeration: row.agglomeration,
    city: row.city,
    address: row.address,
    width: row.widthM,
    height: row.heightM,
    audience: row.audienceEstimate,
    notes: row.notes,
    noPhoto: row.hasPhoto,
  };
  return values[fieldId];
}

/**
 * `_canViewSupplierDetails` is accepted for call-site compatibility (callers
 * still thread the same permission value through for filter-field gating,
 * if any) but is no longer used here — the supplier.view-gated columns stay
 * present for every caller and mask per row instead (field-visibility).
 */
export function buildDecisionSharedColumns(
  t: TFunction<'offers'>,
  _canViewSupplierDetails: boolean,
) {
  const ids = [
    'mediaType',
    'format',
    'supplier',
    'supplierNumber',
    'grwId',
    'region',
    'county',
    'agglomeration',
    'city',
    'address',
    'dimensions',
    'audienceEstimate',
    'notes',
    'noPhoto',
  ] as const;
  return buildMediaUnitFieldColumns<UnitDecisionsListItem>({
    surface: 'decisions',
    adapter: {
      getValue,
      getDimensionNotes: (row) => row.dimensionNotes,
      getFormatLabel: (row) => row.format,
      getSupplierWithheld: (row) => row.supplierWithheld,
    },
    labels: {
      columns: Object.fromEntries(
        ids.map((id) => [id, t(`table.columns.${id}`, { ns: 'inventory' })]),
      ),
      trueLabel: t('bool.yes', { ns: 'common' }),
      falseLabel: t('bool.no', { ns: 'common' }),
      noPhotoAria: t('table.noPhotoAria', { ns: 'inventory' }),
    },
    supplierMaskedExplanation: t('workspace.unitsTab.supplierMaskedExplanation'),
    sortKeys: OFFER_MEDIA_UNIT_SORT_KEY_ALIASES,
  });
}
